/* -----------------Globals--------------- */
import { forwardRef, useImperativeHandle, useRef } from "react";
import { Platform } from "react-native";

/* -----------------UI--------------- */
import { Caption, HelperText, TextInput } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../useAppTheme";

/* -----------------Types--------------- */
import type { FeedbackParams } from "./types";

import { Field, Form } from "houseform";
/* -----------------Child components--------------- */
import Box from "../Box";

/* -----------------Libraries--------------- */
import { z } from "zod";

const MAX_FEEDBACK_LENGTH = 1000;

type FeedbackFormRef = {
	submit: () => void;
};

type FeedbackFormProps = {
	feedbackDialogParams: FeedbackParams;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: string;
		value: string;
	}[];
	onSubmit: (feedback: string, email: string) => void;
	onEmailValidationChange: (isValid: boolean) => void;
	onFeedbackValidationChange: (isValid: boolean) => void;
	onEmailTextChange: (text: string) => void;
	onFeedbackTextChange: (text: string) => void;
	feedbackLimitError: string;
	feedbackPlaceholder: string;
};

export const FeedbackForm = forwardRef<FeedbackFormRef, FeedbackFormProps>(
	(
		{
			feedbackDialogParams,
			feedbackReasonList,
			onSubmit,
			onEmailValidationChange,
			onFeedbackValidationChange,
			onEmailTextChange,
			onFeedbackTextChange,
			feedbackLimitError,
			feedbackPlaceholder,
		},
		ref,
	) => {
		const submitRef = useRef<(() => void) | null>(null);
		const theme = useAppTheme();

		useImperativeHandle(ref, () => ({
			submit: () => {
				if (submitRef.current) {
					submitRef.current();
				}
			},
		}));

		return (
			<Form
				onSubmit={(values) => {
					onSubmit(values.feedback, values.email);
				}}
			>
				{({ submit }) => {
					// Store the submit function so we can expose it via ref
					submitRef.current = submit;

					return (
						<Box gap={24} width="100%">
							{!feedbackDialogParams?.email && (
								<Field
									name="email"
									onChangeValidate={z
										.string()
										.email("Please enter a valid email address")}
								>
									{({ value, setValue, onBlur, errors, isValid }) => {
										// Notify parent of email validation state
										onEmailValidationChange(isValid);

										return (
											<Box>
												<TextInput
													label="Email"
													mode="outlined"
													placeholder="e.g., dave@gmail.com"
													onChangeText={(text) => {
														setValue(text);
														onEmailTextChange(text);
													}}
													autoFocus
													value={value}
													onBlur={onBlur}
													autoCapitalize="none"
													keyboardType="email-address"
													onSubmitEditing={submit}
													error={errors.length > 0}
												/>
												{errors.map((error) => (
													<HelperText type="error" key={error}>
														{error}
													</HelperText>
												))}
											</Box>
										);
									}}
								</Field>
							)}
							<Field
								name="feedback"
								onChangeValidate={z
									.string()
									.min(feedbackReasonList ? 0 : 10, feedbackLimitError)}
							>
								{({ value, setValue, onBlur, errors, isValid }) => {
									// Notify parent of feedback validation state
									onFeedbackValidationChange(isValid);

									return (
										<Box>
											<TextInput
												multiline
												mode="outlined"
												label="Feedback"
												maxLength={MAX_FEEDBACK_LENGTH}
												autoFocus
												error={errors.length > 0}
												style={{
													backgroundColor: theme.colors.surface,
													paddingTop: Platform.OS === "android" ? 16 : "auto",
													height: 200,
												}}
												onBlur={onBlur}
												placeholder={feedbackPlaceholder}
												value={value}
												onChangeText={(text) => {
													setValue(text);
													onFeedbackTextChange(text);
												}}
												onSubmitEditing={submit}
											/>
											{errors.map((error) => (
												<HelperText type="error" key={error}>
													{error}
												</HelperText>
											))}
											<Caption
												style={{
													textAlign: "right",
												}}
											>
												{value.length}/{MAX_FEEDBACK_LENGTH}
											</Caption>
										</Box>
									);
								}}
							</Field>
						</Box>
					);
				}}
			</Form>
		);
	},
);

FeedbackForm.displayName = "FeedbackForm";
